import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Check,
  CheckCheck,
  ChevronLeft,
  Clock,
  FileText,
  Loader2,
  MessageSquare,
  Paperclip,
  Phone,
  Search,
  Send,
  Smile,
  User,
  Package,
  StickyNote,
  Activity,
  X,
  XCircle,
  Zap,
  Hash,
} from 'lucide-react';
import {
  whatsappApi,
  type WhatsAppConversation,
  type WhatsAppInboxTemplate,
  type WhatsAppMessage,
} from '@/lib/whatsappApi';
import { cn } from '@/lib/utils';

/* ═══════════════════════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════════════════════ */

const LIST_POLL_MS = 8_000;
const THREAD_POLL_MS = 4_000;

const AVATAR_PALETTE = [
  'bg-indigo-600','bg-violet-600','bg-sky-600','bg-emerald-600',
  'bg-amber-600','bg-rose-600','bg-teal-600','bg-cyan-600',
  'bg-fuchsia-600','bg-orange-600','bg-lime-600','bg-pink-600',
];

type FilterId = 'all'|'unread'|'pending'|'confirmed'|'shipped'|'delivered'|'cancelled'|'vip';

const FILTERS: { id: FilterId; label: string; emoji?: string }[] = [
  { id: 'all',       label: 'All' },
  { id: 'unread',    label: 'Unread' },
  { id: 'pending',   label: 'Pending' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'shipped',   label: 'Shipped' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'vip',       label: 'VIP', emoji: '⭐' },
];

function applyFilter(c: WhatsAppConversation, f: FilterId): boolean {
  switch (f) {
    case 'unread':    return c.unreadCount > 0;
    case 'pending':   return ['pending','sent'].includes(c.lastStatus);
    case 'confirmed': return c.lastDirection === 'inbound';
    case 'delivered': return c.lastStatus === 'delivered';
    case 'shipped':   return c.lastStatus === 'delivered' && c.lastDirection === 'outbound';
    case 'cancelled': return c.lastStatus === 'failed';
    case 'vip':       return true;
    default:          return true;
  }
}

/* ═══════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════ */

function formatPhone(phone: string): string {
  const d = phone.replace(/\D/g, '');
  if (d.startsWith('20') && d.length >= 12)
    return `+${d.slice(0,2)} ${d.slice(2,5)} ${d.slice(5,8)} ${d.slice(8)}`;
  return d ? `+${d}` : phone;
}

function formatTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000)     return 'now';
  if (diff < 3_600_000)  return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diff < 604_800_000) return new Date(iso).toLocaleDateString([], { weekday: 'short' });
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function msgTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function dateDividerLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yest  = new Date(); yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yest.toDateString())  return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}

function initials(name: string | null, phone: string): string {
  if (name?.trim()) {
    const p = name.trim().split(/\s+/);
    return p.length >= 2
      ? (p[0][0] + p[p.length - 1][0]).toUpperCase()
      : name.trim().slice(0, 2).toUpperCase();
  }
  return phone.replace(/\D/g, '').slice(-2) || '??';
}

function avatarColor(phone: string): string {
  let h = 0;
  for (let i = 0; i < phone.length; i++) h = ((h << 5) - h + phone.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

function displayName(c: WhatsAppConversation) {
  return c.customerName?.trim() || formatPhone(c.customerPhone);
}

function needsDateDivider(msgs: WhatsAppMessage[], i: number) {
  if (i === 0) return true;
  return new Date(msgs[i].createdAt).toDateString() !== new Date(msgs[i - 1].createdAt).toDateString();
}

/* ═══════════════════════════════════════════════════════════════
   Shared tiny components
   ═══════════════════════════════════════════════════════════════ */

function Avi({ name, phone, size = 'md' }: { name: string | null; phone: string; size?: 'sm'|'md'|'lg' }) {
  const s = size === 'lg' ? 'h-12 w-12 text-sm' : size === 'sm' ? 'h-8 w-8 text-[10px]' : 'h-10 w-10 text-xs';
  return (
    <div className={cn('shrink-0 rounded-full flex items-center justify-center font-semibold text-white/90 select-none', avatarColor(phone), s)}>
      {initials(name, phone)}
    </div>
  );
}

function Ticks({ status }: { status: string }) {
  if (status === 'failed')    return <XCircle   className="h-3 w-3 text-red-400 shrink-0" />;
  if (status === 'read')      return <CheckCheck className="h-3 w-3 text-sky-400 shrink-0" />;
  if (status === 'delivered') return <CheckCheck className="h-3 w-3 text-zinc-400 shrink-0" />;
  if (status === 'sent')      return <Check      className="h-3 w-3 text-zinc-500 shrink-0" />;
  return <Clock className="h-3 w-3 text-zinc-600 shrink-0" />;
}

/* ═══════════════════════════════════════════════════════════════
   Message bubble
   ═══════════════════════════════════════════════════════════════ */

function Bubble({ message: m }: { message: WhatsAppMessage }) {
  const out = m.direction === 'outbound';

  if (m.messageType === 'system') {
    return (
      <div className="flex justify-center py-1">
        <span className="text-[11px] text-zinc-500 bg-zinc-800/50 rounded-full px-3 py-1 select-none">
          {m.bodyPreview || 'System'}
        </span>
      </div>
    );
  }

  return (
    <div className={cn('flex', out ? 'justify-end' : 'justify-start')} dir="auto">
      <div
        className={cn(
          'relative max-w-[75%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed',
          out
            ? 'bg-indigo-600 text-white rounded-br-md shadow-lg shadow-indigo-950/25'
            : 'bg-zinc-800/80 text-zinc-100 rounded-bl-md border border-white/[0.05]',
        )}
      >
        {m.messageType === 'template' && m.templateKey && (
          <div className={cn(
            'flex items-center gap-1 mb-1.5 text-[10px] font-semibold tracking-wide uppercase',
            out ? 'text-indigo-200/80' : 'text-zinc-500',
          )}>
            <Zap className="h-2.5 w-2.5" />
            {m.templateKey}
          </div>
        )}

        <p className="whitespace-pre-wrap break-words">{m.bodyPreview || '\u2014'}</p>

        <div className={cn(
          'flex items-center justify-end gap-1.5 mt-1 text-[10px]',
          out ? 'text-indigo-200/60' : 'text-zinc-500',
        )}>
          <span>{msgTime(m.createdAt)}</span>
          {out && <Ticks status={m.status} />}
        </div>

        {m.status === 'failed' && m.errorMessage && (
          <p className="mt-1.5 text-[11px] text-red-300/80 border-t border-red-400/20 pt-1.5">
            {m.errorMessage}
          </p>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Conversation card (left list)
   ═══════════════════════════════════════════════════════════════ */

function ConvCard({
  c,
  active,
  onClick,
}: {
  c: WhatsAppConversation;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left px-3 py-3.5 transition-all duration-150 border-l-2',
        active
          ? 'bg-white/[0.06] border-l-indigo-500'
          : 'border-l-transparent hover:bg-white/[0.025]',
      )}
    >
      <div className="flex gap-3">
        <Avi name={c.customerName} phone={c.customerPhone} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className={cn('text-[13px] font-medium truncate', c.unreadCount ? 'text-white' : 'text-zinc-300')}>
              {displayName(c)}
            </span>
            <span className="text-[11px] text-zinc-600 shrink-0 tabular-nums leading-none">
              {formatTime(c.lastMessageAt)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2 mt-1">
            <p className={cn('text-[12px] truncate flex-1', c.unreadCount ? 'text-zinc-400' : 'text-zinc-600')}>
              {c.lastDirection === 'outbound' && <span className="text-zinc-600">You: </span>}
              {c.lastMessagePreview}
            </p>
            {c.unreadCount > 0 && (
              <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-500 text-[10px] font-bold text-white flex items-center justify-center leading-none">
                {c.unreadCount > 99 ? '99+' : c.unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Right panel — Customer context
   ═══════════════════════════════════════════════════════════════ */

type CtxTab = 'customer'|'orders'|'notes'|'timeline';

const CTX_TABS: { id: CtxTab; label: string; Icon: typeof User }[] = [
  { id: 'customer', label: 'Customer', Icon: User },
  { id: 'orders',   label: 'Orders',   Icon: Package },
  { id: 'notes',    label: 'Notes',    Icon: StickyNote },
  { id: 'timeline', label: 'Timeline', Icon: Activity },
];

function ContextPanel({
  conv,
  msgs,
  phone,
}: {
  conv: WhatsAppConversation | undefined;
  msgs: WhatsAppMessage[];
  phone: string;
}) {
  const [tab, setTab] = useState<CtxTab>('customer');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    setNotes(localStorage.getItem(`nex-notes-${phone}`) || '');
  }, [phone]);

  const saveNotes = useCallback((v: string) => {
    setNotes(v);
    localStorage.setItem(`nex-notes-${phone}`, v);
  }, [phone]);

  const stats = useMemo(() => {
    const inb  = msgs.filter(m => m.direction === 'inbound').length;
    const outb = msgs.filter(m => m.direction === 'outbound').length;
    const tpl  = msgs.filter(m => m.messageType === 'template').length;
    const dlv  = msgs.filter(m => m.status === 'delivered' || m.status === 'read').length;
    const fail = msgs.filter(m => m.status === 'failed').length;
    const oids = [...new Set(msgs.filter(m => m.orderId).map(m => m.orderId!))];
    return { inb, outb, tpl, dlv, fail, oids, total: msgs.length };
  }, [msgs]);

  const orderMap = useMemo(() => {
    const map = new Map<string, WhatsAppMessage[]>();
    for (const m of msgs) if (m.orderId) map.set(m.orderId, [...(map.get(m.orderId) || []), m]);
    return map;
  }, [msgs]);

  const timeline = useMemo(() => {
    const ev: { t: string; label: string; dir: 'in'|'out'|'sys' }[] = [];
    for (const m of msgs) {
      if (m.messageType === 'template')
        ev.push({ t: m.createdAt, label: `Template: ${m.templateKey || '?'}`, dir: 'out' });
      else if (m.direction === 'inbound')
        ev.push({ t: m.createdAt, label: (m.bodyPreview || 'Message').slice(0, 50), dir: 'in' });
    }
    return ev.slice(-25).reverse();
  }, [msgs]);

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 p-3 border-b border-white/[0.06]">
        <div className="flex bg-elevated rounded-lg p-0.5">
          {CTX_TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 px-1 py-[7px] rounded-md text-[11px] font-medium transition-colors',
                tab === id ? 'bg-white/[0.08] text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300',
              )}
            >
              <Icon className="h-3 w-3" />
              <span className="hidden xl:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-4">

        {/* ── Customer ── */}
        {tab === 'customer' && (
          <>
            <div className="flex flex-col items-center text-center pb-4 border-b border-white/[0.04]">
              <Avi name={conv?.customerName ?? null} phone={phone} size="lg" />
              <h3 className="mt-3 text-sm font-semibold text-white">{conv?.customerName || formatPhone(phone)}</h3>
              <p className="text-xs text-zinc-500 mt-0.5 font-mono">{formatPhone(phone)}</p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {([
                ['Messages', stats.total,      'text-white'],
                ['Orders',   stats.oids.length, 'text-white'],
                ['Delivered', stats.dlv,        'text-emerald-400'],
                ['Failed',    stats.fail,       'text-red-400'],
              ] as const).map(([lbl, val, clr]) => (
                <div key={lbl} className="bg-elevated/50 rounded-lg p-3 border border-white/[0.04]">
                  <p className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider">{lbl}</p>
                  <p className={cn('text-lg font-semibold mt-1 tabular-nums', clr)}>{val}</p>
                </div>
              ))}
            </div>

            <div className="space-y-3 pt-2">
              {([
                ['Inbound',        stats.inb],
                ['Outbound',       stats.outb],
                ['Templates sent', stats.tpl],
              ] as const).map(([lbl, val]) => (
                <div key={lbl} className="flex justify-between items-center">
                  <span className="text-[11px] text-zinc-500">{lbl}</span>
                  <span className="text-xs text-zinc-300 tabular-nums">{val}</span>
                </div>
              ))}
              {msgs.length > 0 && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] text-zinc-500">First contact</span>
                    <span className="text-xs text-zinc-300">{new Date(msgs[0].createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] text-zinc-500">Last message</span>
                    <span className="text-xs text-zinc-300">{formatTime(msgs[msgs.length - 1].createdAt)}</span>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* ── Orders ── */}
        {tab === 'orders' && (
          stats.oids.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Package className="h-8 w-8 text-zinc-700 mb-3" />
              <p className="text-xs text-zinc-500">No orders linked</p>
            </div>
          ) : (
            <div className="space-y-2">
              {stats.oids.map(oid => {
                const om = orderMap.get(oid) || [];
                const last = om[om.length - 1];
                return (
                  <div key={oid} className="bg-elevated/50 rounded-lg p-3 border border-white/[0.04]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Hash className="h-3 w-3 text-zinc-500" />
                        <span className="text-xs font-medium text-white">{oid}</span>
                      </div>
                      <span className={cn(
                        'text-[10px] font-medium px-2 py-0.5 rounded-full capitalize',
                        last?.status === 'delivered' || last?.status === 'read'
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : last?.status === 'failed'
                            ? 'bg-red-500/10 text-red-400'
                            : 'bg-zinc-700/50 text-zinc-400',
                      )}>
                        {last?.status || 'unknown'}
                      </span>
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-1.5">
                      {om.length} message{om.length !== 1 ? 's' : ''}
                    </p>
                    {last && <p className="text-[11px] text-zinc-600 mt-0.5">{new Date(last.createdAt).toLocaleDateString()}</p>}
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* ── Notes ── */}
        {tab === 'notes' && (
          <div className="space-y-2">
            <textarea
              value={notes}
              onChange={e => saveNotes(e.target.value)}
              placeholder="Add notes about this customer…"
              className="w-full h-52 resize-none rounded-lg bg-elevated/50 border border-white/[0.06] text-sm text-zinc-200 px-3 py-2.5 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/40 focus:border-indigo-500/20 transition-colors"
              dir="auto"
            />
            <p className="text-[10px] text-zinc-600">Saved locally in this browser</p>
          </div>
        )}

        {/* ── Timeline ── */}
        {tab === 'timeline' && (
          timeline.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Activity className="h-8 w-8 text-zinc-700 mb-3" />
              <p className="text-xs text-zinc-500">No activity yet</p>
            </div>
          ) : (
            <div className="relative pl-5">
              <div className="absolute left-[7px] top-2 bottom-2 w-px bg-white/[0.06]" />
              {timeline.map((ev, i) => (
                <div key={i} className="relative flex gap-3 pb-5">
                  <div className={cn(
                    'absolute left-[-13px] top-1.5 h-2 w-2 rounded-full ring-2 ring-panel',
                    ev.dir === 'in' ? 'bg-emerald-500' : ev.dir === 'out' ? 'bg-indigo-500' : 'bg-zinc-600',
                  )} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-zinc-300 break-words">{ev.label}</p>
                    <p className="text-[10px] text-zinc-600 mt-0.5">{new Date(ev.t).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ▌▌  MAIN COMPONENT  ▌▌
   ═══════════════════════════════════════════════════════════════ */

export function ConversationsInbox({ connected }: { connected: boolean }) {

  /* ── List state ── */
  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [listLoading, setListLoading]     = useState(true);
  const [search, setSearch]               = useState('');
  const [searchD, setSearchD]             = useState('');
  const [filter, setFilter]               = useState<FilterId>('all');
  const [page, setPage]                   = useState(1);
  const [totalPages, setTotalPages]       = useState(1);

  /* ── Thread state ── */
  const [selPhone, setSelPhone]           = useState<string | null>(null);
  const [msgs, setMsgs]                   = useState<WhatsAppMessage[]>([]);
  const [threadName, setThreadName]       = useState<string | null>(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [hasMore, setHasMore]             = useState(false);
  const [loadingMore, setLoadingMore]     = useState(false);

  /* ── Compose state ── */
  const [compose, setCompose]             = useState('');
  const [sending, setSending]             = useState(false);
  const [tpls, setTpls]                   = useState<WhatsAppInboxTemplate[]>([]);
  const [showTpl, setShowTpl]             = useState(false);
  const [selTpl, setSelTpl]               = useState('');
  const [tplParams, setTplParams]         = useState<string[]>([]);

  /* ── UI state ── */
  const [ctxOpen, setCtxOpen] = useState(true);

  /* ── Refs ── */
  const listSinceRef   = useRef<string | null>(null);
  const threadSinceRef = useRef<string | null>(null);
  const bottomRef      = useRef<HTMLDivElement>(null);
  const searchRef      = useRef<HTMLInputElement>(null);
  const composeRef     = useRef<HTMLTextAreaElement>(null);

  /* ── Debounced search ── */
  useEffect(() => { const t = setTimeout(() => setSearchD(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  /* ── Load list ── */
  const loadList = useCallback(async (o?: { silent?: boolean; reset?: boolean }) => {
    if (!connected) { setListLoading(false); return; }
    if (!o?.silent) setListLoading(true);
    try {
      const p = o?.reset ? 1 : page;
      const r = await whatsappApi.conversations({ q: searchD || undefined, page: p, limit: 30, since: o?.silent ? listSinceRef.current || undefined : undefined });
      listSinceRef.current = r.data.serverTime;
      if (o?.silent && !o?.reset) {
        setConversations(prev => {
          const m = new Map(prev.map(c => [c.customerPhone, c]));
          for (const c of r.data.conversations) m.set(c.customerPhone, c);
          return Array.from(m.values()).sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());
        });
      } else {
        setConversations(r.data.conversations);
      }
      setTotalPages(r.data.pagination.totalPages);
      if (o?.reset) setPage(1);
    } catch (e: unknown) {
      if (!o?.silent) toast.error(e instanceof Error ? e.message : 'Load failed');
    } finally { setListLoading(false); }
  }, [connected, page, searchD]);

  /* ── Load thread ── */
  const loadThread = useCallback(async (ph: string, o?: { silent?: boolean; before?: string }) => {
    if (!connected) return;
    if (!o?.silent && !o?.before) setThreadLoading(true);
    try {
      const r = await whatsappApi.conversationMessages(ph, { limit: 50, before: o?.before, since: o?.silent && !o?.before ? threadSinceRef.current || undefined : undefined });
      threadSinceRef.current = r.data.serverTime;
      setThreadName(r.data.customerName);
      if (o?.before) {
        setMsgs(prev => [...r.data.messages, ...prev]);
      } else if (o?.silent) {
        setMsgs(prev => { const map = new Map(prev.map(x => [x.id, x])); for (const x of r.data.messages) map.set(x.id, x); return Array.from(map.values()).sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt)); });
      } else {
        setMsgs(r.data.messages);
      }
      setHasMore(r.data.hasMore);
      if (!o?.before) {
        await whatsappApi.markConversationRead(ph);
        setConversations(prev => prev.map(c => c.customerPhone === ph ? { ...c, unreadCount: 0 } : c));
      }
    } catch (e: unknown) {
      if (!o?.silent) toast.error(e instanceof Error ? e.message : 'Load failed');
    } finally { setThreadLoading(false); setLoadingMore(false); }
  }, [connected]);

  /* ── Effects ── */
  useEffect(() => { loadList({ reset: true }); }, [searchD]);
  useEffect(() => { loadList(); }, [page]);
  useEffect(() => { if (!connected) return; const iv = setInterval(() => loadList({ silent: true }), LIST_POLL_MS); return () => clearInterval(iv); }, [connected, loadList]);
  useEffect(() => { if (!selPhone) return; threadSinceRef.current = null; loadThread(selPhone); const iv = setInterval(() => loadThread(selPhone, { silent: true }), THREAD_POLL_MS); return () => clearInterval(iv); }, [selPhone]);
  useEffect(() => { if (!showTpl || !connected) return; whatsappApi.inboxTemplates().then(r => setTpls(r.data.templates)).catch(() => setTpls([])); }, [showTpl, connected]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs.length, selPhone]);
  useEffect(() => { const h = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); searchRef.current?.focus(); } }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, []);

  /* ── Derived ── */
  const filtered = useMemo(() => conversations.filter(c => applyFilter(c, filter)), [conversations, filter]);
  const counts   = useMemo(() => {
    const r: Record<FilterId, number> = { all: 0, unread: 0, pending: 0, confirmed: 0, shipped: 0, delivered: 0, cancelled: 0, vip: 0 };
    for (const c of conversations) { r.all++; for (const f of FILTERS) if (f.id !== 'all' && applyFilter(c, f.id)) r[f.id]++; }
    return r;
  }, [conversations]);
  const selConv = conversations.find(c => c.customerPhone === selPhone);
  const tplMeta = tpls.find(t => t.key === selTpl);

  /* ── Handlers ── */
  const sendText = async () => {
    if (!selPhone || !compose.trim()) return;
    setSending(true);
    try {
      const r = await whatsappApi.sendConversationText(selPhone, compose.trim());
      setMsgs(prev => [...prev, r.data.message]);
      setCompose('');
      if (composeRef.current) { composeRef.current.style.height = 'auto'; }
      composeRef.current?.focus();
      loadList({ silent: true });
    } catch (e: unknown) { toast.error(e instanceof Error ? e.message : 'Send failed'); }
    finally { setSending(false); }
  };

  const sendTemplate = async () => {
    if (!selPhone || !selTpl) return;
    setSending(true);
    try {
      const r = await whatsappApi.sendConversationTemplate(selPhone, { templateKey: selTpl, bodyParameters: tplParams.filter(Boolean).length ? tplParams : undefined });
      setMsgs(prev => [...prev, r.data.message]);
      setShowTpl(false); setSelTpl(''); setTplParams([]);
      loadList({ silent: true });
      toast.success('Template sent');
    } catch (e: unknown) { toast.error(e instanceof Error ? e.message : 'Send failed'); }
    finally { setSending(false); }
  };

  const pick = (ph: string) => { setSelPhone(ph); setCompose(''); setShowTpl(false); };

  /* ═══════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════ */
  return (
    <div className="flex h-full bg-surface overflow-hidden">

      {/* ╔══════════════════════════════════════════════════════╗
         ║  LEFT — Conversation list                          ║
         ╚══════════════════════════════════════════════════════╝ */}
      <aside className={cn('w-80 shrink-0 flex flex-col border-r border-white/[0.06]', selPhone && 'hidden md:flex')}>

        {/* Search + Filters */}
        <div className="shrink-0 p-3 space-y-3 border-b border-white/[0.04]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-600 pointer-events-none" />
            <input
              ref={searchRef}
              type="search"
              placeholder="Search conversations…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full h-9 rounded-lg bg-elevated border border-white/[0.06] pl-9 pr-8 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/40 focus:border-indigo-500/20 transition-colors"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex gap-1 overflow-x-auto scrollbar-none -mx-1 px-1 pb-0.5">
            {FILTERS.map(f => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={cn(
                  'shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors whitespace-nowrap',
                  filter === f.id
                    ? 'bg-indigo-500/15 text-indigo-300 ring-1 ring-indigo-500/20'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]',
                )}
              >
                {f.emoji && <span className="mr-0.5">{f.emoji}</span>}
                {f.label}
                {counts[f.id] > 0 && f.id !== 'all' && (
                  <span className="ml-1 opacity-50 tabular-nums">{counts[f.id]}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {listLoading && !conversations.length ? (
            <div className="p-3 space-y-1">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="flex gap-3 px-3 py-3.5 animate-pulse">
                  <div className="h-10 w-10 rounded-full bg-zinc-800/80" />
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-3 w-24 rounded bg-zinc-800/60" />
                    <div className="h-2.5 w-36 rounded bg-zinc-800/40" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
              <div className="w-12 h-12 rounded-2xl bg-zinc-800/30 border border-white/[0.04] flex items-center justify-center mb-3">
                <MessageSquare className="h-5 w-5 text-zinc-700" />
              </div>
              <p className="text-xs text-zinc-500">
                {search ? 'No results' : filter !== 'all' ? `No ${filter} conversations` : 'No conversations yet'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.03]">
              {filtered.map(c => (
                <ConvCard key={c.customerPhone} c={c} active={selPhone === c.customerPhone} onClick={() => pick(c.customerPhone)} />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="shrink-0 px-3 py-2 border-t border-white/[0.06] flex items-center justify-between text-[11px] text-zinc-600">
            <button type="button" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-2 py-1 rounded hover:bg-white/[0.04] disabled:opacity-30 transition-colors">&larr; Prev</button>
            <span className="tabular-nums">{page}/{totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-2 py-1 rounded hover:bg-white/[0.04] disabled:opacity-30 transition-colors">Next &rarr;</button>
          </div>
        )}
      </aside>

      {/* ╔══════════════════════════════════════════════════════╗
         ║  CENTER — Chat workspace                           ║
         ╚══════════════════════════════════════════════════════╝ */}
      <section className={cn('flex-1 flex flex-col min-w-0', !selPhone && 'hidden md:flex')}>

        {!selPhone ? (
          /* Empty state */
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <div className="w-16 h-16 rounded-2xl bg-zinc-800/40 border border-white/[0.04] flex items-center justify-center mb-5">
              <MessageSquare className="h-7 w-7 text-zinc-700" />
            </div>
            <h3 className="text-sm font-medium text-zinc-400">Select a conversation</h3>
            <p className="text-xs text-zinc-600 mt-1.5 max-w-[260px]">Choose a conversation from the list to start messaging</p>
            <div className="flex items-center gap-1.5 mt-5 text-[10px] text-zinc-700">
              <kbd className="px-1.5 py-0.5 rounded bg-zinc-800 border border-white/[0.06] font-mono">&thinsp;&#8984;K&thinsp;</kbd>
              <span>to search</span>
            </div>
          </div>
        ) : (
          <>
            {/* ── Header ── */}
            <header className="shrink-0 h-[60px] px-4 border-b border-white/[0.06] bg-panel/50 backdrop-blur-md flex items-center gap-3">
              <button type="button" className="md:hidden p-1.5 -ml-1 rounded-lg text-zinc-400 hover:text-white hover:bg-white/[0.04]" onClick={() => setSelPhone(null)}>
                <ChevronLeft className="h-5 w-5" />
              </button>
              <Avi name={selConv?.customerName || threadName || null} phone={selPhone} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-[13px] font-semibold text-white truncate">{threadName || selConv?.customerName || formatPhone(selPhone)}</h3>
                  <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" title="Online" />
                </div>
                <p className="text-[11px] text-zinc-500 font-mono">{formatPhone(selPhone)}</p>
              </div>
              <div className="flex items-center gap-0.5">
                <button type="button" className="p-2 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04] transition-colors" title="Call">
                  <Phone className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setCtxOpen(v => !v)}
                  className={cn('p-2 rounded-lg transition-colors hidden lg:inline-flex', ctxOpen ? 'text-indigo-400 bg-indigo-500/10' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]')}
                  title="Customer info"
                >
                  <User className="h-4 w-4" />
                </button>
              </div>
            </header>

            {/* ── Messages ── */}
            <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-4 space-y-1.5" style={{ background: 'linear-gradient(180deg, #09090b 0%, #0a0a0d 100%)' }}>
              {hasMore && (
                <div className="flex justify-center pb-3">
                  <button
                    type="button"
                    disabled={loadingMore}
                    onClick={() => { const o = msgs[0]; if (!o) return; setLoadingMore(true); loadThread(selPhone, { before: o.id }); }}
                    className="text-[11px] text-zinc-600 hover:text-zinc-400 px-3 py-1.5 rounded-full border border-white/[0.04] hover:border-white/[0.08] transition-colors"
                  >
                    {loadingMore ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Load older messages'}
                  </button>
                </div>
              )}
              {threadLoading && !msgs.length ? (
                <div className="flex flex-col items-center justify-center py-24 gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-zinc-600" />
                  <p className="text-[11px] text-zinc-600">Loading messages\u2026</p>
                </div>
              ) : (
                msgs.map((m, i) => (
                  <div key={m.id}>
                    {needsDateDivider(msgs, i) && (
                      <div className="flex items-center gap-3 py-3">
                        <div className="flex-1 h-px bg-white/[0.04]" />
                        <span className="text-[11px] font-medium text-zinc-500 select-none">{dateDividerLabel(m.createdAt)}</span>
                        <div className="flex-1 h-px bg-white/[0.04]" />
                      </div>
                    )}
                    <Bubble message={m} />
                  </div>
                ))
              )}
              <div ref={bottomRef} />
            </div>

            {/* ── Composer ── */}
            <footer className="shrink-0 px-4 py-3 border-t border-white/[0.06] bg-panel/70 backdrop-blur-md">
              {showTpl && (
                <div className="mb-3 rounded-xl border border-white/[0.06] bg-elevated p-3 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-zinc-400">Quick reply template</span>
                    <button type="button" onClick={() => setShowTpl(false)} className="text-zinc-600 hover:text-zinc-400"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <select
                    value={selTpl}
                    onChange={e => { setSelTpl(e.target.value); const t = tpls.find(x => x.key === e.target.value); setTplParams(Array(t?.bodyVariableCount || 0).fill('')); }}
                    className="w-full h-8 rounded-lg bg-surface border border-white/[0.06] text-xs text-zinc-300 px-2.5 focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
                  >
                    <option value="">Choose template\u2026</option>
                    {tpls.map(t => <option key={t.key} value={t.key}>{t.label || t.key} ({t.languageCode})</option>)}
                  </select>
                  {tplMeta && tplMeta.bodyVariableCount > 0 && (
                    <div className="space-y-1.5">
                      {tplParams.map((_, i) => (
                        <input key={i} placeholder={`Variable {{${i + 1}}}`} value={tplParams[i] || ''} onChange={e => { const n = [...tplParams]; n[i] = e.target.value; setTplParams(n); }}
                          className="w-full h-7 rounded-md bg-surface border border-white/[0.06] text-xs text-zinc-300 px-2.5 focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
                        />
                      ))}
                    </div>
                  )}
                  <button type="button" onClick={sendTemplate} disabled={sending || !selTpl}
                    className="w-full h-8 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-xs font-medium text-white transition-colors">
                    {sending ? 'Sending\u2026' : 'Send template'}
                  </button>
                </div>
              )}

              <div className="flex items-end gap-2">
                <div className="flex-1 flex items-end rounded-xl bg-elevated border border-white/[0.06] focus-within:ring-1 focus-within:ring-indigo-500/30 focus-within:border-indigo-500/20 transition-all">
                  <textarea
                    ref={composeRef}
                    value={compose}
                    onChange={e => setCompose(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendText(); } }}
                    onInput={e => { const t = e.target as HTMLTextAreaElement; t.style.height = 'auto'; t.style.height = Math.min(t.scrollHeight, 120) + 'px'; }}
                    placeholder="Type a message\u2026"
                    rows={1}
                    dir="auto"
                    className="flex-1 resize-none bg-transparent text-[13.5px] text-zinc-200 px-4 py-2.5 placeholder:text-zinc-600 focus:outline-none min-h-[40px] max-h-[120px]"
                  />
                  <div className="flex items-center gap-0.5 px-1.5 pb-1.5 shrink-0">
                    <button type="button" className="p-1.5 rounded-md text-zinc-600 hover:text-zinc-400 hover:bg-white/[0.04] transition-colors" title="Emoji"><Smile className="h-4 w-4" /></button>
                    <button type="button" className="p-1.5 rounded-md text-zinc-600 hover:text-zinc-400 hover:bg-white/[0.04] transition-colors" title="Attach"><Paperclip className="h-4 w-4" /></button>
                    <button type="button" onClick={() => setShowTpl(v => !v)} className={cn('p-1.5 rounded-md transition-colors', showTpl ? 'text-indigo-400 bg-indigo-500/10' : 'text-zinc-600 hover:text-zinc-400 hover:bg-white/[0.04]')} title="Templates"><Zap className="h-4 w-4" /></button>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={sending || !compose.trim()}
                  onClick={() => void sendText()}
                  className={cn(
                    'shrink-0 h-10 w-10 rounded-xl flex items-center justify-center transition-all duration-200',
                    compose.trim()
                      ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-900/30 scale-100'
                      : 'bg-zinc-800/80 text-zinc-600 scale-95',
                  )}
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </footer>
          </>
        )}
      </section>

      {/* ╔══════════════════════════════════════════════════════╗
         ║  RIGHT — Customer context panel                    ║
         ╚══════════════════════════════════════════════════════╝ */}
      {selPhone && ctxOpen && (
        <aside className="hidden lg:flex w-[340px] shrink-0 flex-col border-l border-white/[0.06] bg-panel">
          <ContextPanel conv={selConv} msgs={msgs} phone={selPhone} />
        </aside>
      )}
    </div>
  );
}
